import { useMemo } from "react";
import { useAppSelector } from "../app/hooks";
import { ChatMessageType } from "../contracts";
import { CommercialSimulatorPlanType, getLastToolRequest, isCommercialSimulatorToolRequest } from "../models/toolRequestModels";
import {
    getToolResponseById,
    isCommercialSimulatorSubBrandToolResponse,
    isCommercialSimulatorToolResponse
} from "../models/toolResponseModels";
import {
    selectCurrentCommercialPlan,
    selectMessages,
    selectSimulationSetupSelection
} from "../slices/chatSlice";
import { capitalizeFirstLetter } from "../utils/stringValueConversion";

export const SIMULATION_REQUIRED_FIELD_COUNT = 5;

export type SimulationSetupFieldKey
    = "retailer"
        | "planningLevel"
        | "selection"
        | "timeframe"
        | "goal"
        | "budget";

export type SimulationSetupField = {
    key: SimulationSetupFieldKey;
    label: string;
    placeholder: string;
    value: string | null;
    isComplete: boolean;
    isOptional: boolean;
    guidanceLabel: string;
    nextAction: string;
};

type SimulationReadinessData = {
    fields: SimulationSetupField[];
    completedCount: number;
    requiredCount: number;
    nextAction: string;
    isReady: boolean;
    canGenerate: boolean;
    showReadinessPanel: boolean;
};

const requiredFieldOrder: Array<{
    key: Exclude<SimulationSetupFieldKey, "budget">;
    label: string;
    placeholder: string;
    guidanceLabel: string;
    nextAction: string;
}> = [
    {
        key: "retailer",
        label: "Retailer",
        placeholder: "Woolworths, Coles, Amazon...",
        guidanceLabel: "Retailer",
        nextAction: "Select a Retailer."
    },
    {
        key: "planningLevel",
        label: "Planning Level",
        placeholder: "SKU, Variant, Sub-Brand or Brand",
        guidanceLabel: "Planning Level",
        nextAction: "Select a Planning Level."
    },
    {
        key: "selection",
        label: "Selection",
        placeholder: "Choose product or portfolio",
        guidanceLabel: "Product Selection",
        nextAction: "Choose a Product Selection."
    },
    {
        key: "timeframe",
        label: "Timeframe",
        placeholder: "Define planning horizon",
        guidanceLabel: "Timeframe",
        nextAction: "Choose a Timeframe."
    },
    {
        key: "goal",
        label: "Goal",
        placeholder: "Revenue, Profit, Volume or Market Share",
        guidanceLabel: "Goal",
        nextAction: "Choose a Goal."
    }
];

const formatPlanningLevel = (planType: string | null | undefined): string | null => {
    if (planType === CommercialSimulatorPlanType.Sku) {
        return "SKU";
    }

    if (planType === CommercialSimulatorPlanType.SubBrand) {
        return "Sub-Brand + Pack Size";
    }

    return null;
};

const firstNonEmpty = (...values: Array<string | null | undefined>): string | null => {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }

    return null;
};

const useSimulationReadiness = (): SimulationReadinessData => {
    const chatMessages = useAppSelector(selectMessages);
    const currentPlan = useAppSelector(selectCurrentCommercialPlan);
    const setupSelection = useAppSelector(selectSimulationSetupSelection);

    return useMemo(() => {
        const toolRequestMessage = getLastToolRequest(chatMessages);
        const toolRequestPayload = toolRequestMessage && isCommercialSimulatorToolRequest(toolRequestMessage.payload)
            ? toolRequestMessage.payload
            : undefined;
        const input = toolRequestPayload?.input;
        const toolResponsePayload = getToolResponseById(chatMessages, toolRequestPayload?.id);
        const skuSelection = isCommercialSimulatorToolResponse(toolResponsePayload)
            ? toolResponsePayload.tool_call_user_selection
            : undefined;
        const subBrandSelection = isCommercialSimulatorSubBrandToolResponse(toolResponsePayload)
            ? toolResponsePayload.tool_call_user_selection
            : undefined;

        const retailer = firstNonEmpty(
            setupSelection.retailer,
            skuSelection?.retailer_name,
            subBrandSelection?.products?.[0]?.retailer_name
        );
        const planningLevel = firstNonEmpty(
            setupSelection.planningLevel,
            formatPlanningLevel(toolRequestPayload?.plan_type)
        );
        const selection = firstNonEmpty(
            setupSelection.selection,
            skuSelection?.sku_name,
            subBrandSelection?.sub_brand_name
        );
        const timeframe = firstNonEmpty(
            setupSelection.timeframe,
            input?.plan_time_period_info
        );
        const goal = firstNonEmpty(
            setupSelection.goal,
            input?.objective ? capitalizeFirstLetter(input.objective) : null
        );
        const budget = firstNonEmpty(
            setupSelection.budget,
            input?.plan_budget
        );

        const fieldValues: Record<Exclude<SimulationSetupFieldKey, "budget">, string | null> = {
            retailer,
            planningLevel,
            selection,
            timeframe,
            goal
        };

        const fields: SimulationSetupField[] = [
            ...requiredFieldOrder.map(field => ({
                ...field,
                value: fieldValues[field.key],
                isComplete: Boolean(fieldValues[field.key]),
                isOptional: false
            })),
            {
                key: "budget",
                label: "Budget",
                placeholder: "Optional — Sovra can recommend",
                guidanceLabel: "Budget",
                nextAction: "",
                value: budget,
                isComplete: Boolean(budget),
                isOptional: true
            }
        ];

        const requiredFields = fields.filter(field => !field.isOptional);
        const completedCount = requiredFields.filter(field => field.isComplete).length;
        const isReady = completedCount === SIMULATION_REQUIRED_FIELD_COUNT;
        const nextIncomplete = requiredFields.find(field => !field.isComplete);
        const nextAction = isReady
            ? "Ready — click Generate Simulation below."
            : nextIncomplete?.nextAction ?? "Select a Retailer.";
        const hasStartedGeneration = Boolean(
            chatMessages?.some(message => message.type === ChatMessageType.AgentRequest)
        );

        return {
            fields,
            completedCount,
            requiredCount: SIMULATION_REQUIRED_FIELD_COUNT,
            nextAction,
            isReady,
            canGenerate: isReady && Boolean(setupSelection.canGenerate),
            showReadinessPanel: currentPlan == null && !hasStartedGeneration
        };
    }, [chatMessages, currentPlan, setupSelection]);
};

export default useSimulationReadiness;
